import { Component, Input, Output, EventEmitter, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { PrimeIcons } from 'primeng/api';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Divider } from 'primeng/divider';
import { AuthStore } from '../../auth/services/auth.store';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type SidebarItem =
  | {
      type: 'item';
      label: string;
      routerLink: string;
      icon: string;
      visible?: boolean;
      ownerOnly?: boolean;
    }
  | { type: 'divider' };

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, Button, TooltipModule, Divider],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})
export class SidebarComponent {
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly currentUrl = signal(this.normalizeUrl(this.router.url));

  @Input() visible = true;
  @Input() isMobile = false;
  @Input() isCollapsed = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() collapsedChange = new EventEmitter<boolean>();

  readonly isOwner = this.authStore.isOwner;
  readonly activeMenuLink = computed(() => this.resolveActiveMenuLink(this.currentUrl()));

  readonly menuItems: SidebarItem[] = [
    { type: 'item', label: 'DASHBOARD', routerLink: '/', icon: PrimeIcons.TH_LARGE, visible: true },
    {
      type: 'item',
      label: 'RAPORTY',
      routerLink: '/reports',
      icon: PrimeIcons.CHART_BAR,
      visible: true,
    },
    { type: 'divider' },
    {
      type: 'item',
      label: 'TIMESHEET',
      routerLink: '/timesheet',
      icon: PrimeIcons.CLOCK,
      visible: true,
    },
    {
      type: 'item',
      label: 'KALENDARZ',
      routerLink: '/timesheet/calendar',
      icon: PrimeIcons.CALENDAR,
      visible: true,
    },
    {
      type: 'item',
      label: 'WPIS ZBIORCZY',
      routerLink: '/timesheet/bulk',
      icon: PrimeIcons.USERS,
      visible: true,
      ownerOnly: true,
    },
    {
      type: 'item',
      label: 'TRACKER',
      routerLink: '/tracker',
      icon: PrimeIcons.CALENDAR_CLOCK,
      visible: true,
    },
    { type: 'divider' },
    {
      type: 'item',
      label: 'KLIENCI',
      routerLink: '/clients',
      icon: PrimeIcons.USER,
      visible: true,
      ownerOnly: true,
    },
    {
      type: 'item',
      label: 'PROJEKTY',
      routerLink: '/work-items',
      icon: PrimeIcons.BRIEFCASE,
      visible: true,
      ownerOnly: true,
    },
  ];

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.currentUrl.set(this.normalizeUrl(event.urlAfterRedirects));
      });
  }

  toggleCollapse(): void {
    this.collapsedChange.emit(!this.isCollapsed);
  }

  isMenuItemActive(item: Extract<SidebarItem, { type: 'item' }>): boolean {
    if (!item.visible || (item.ownerOnly && !this.isOwner())) {
      return false;
    }

    return this.activeMenuLink() === item.routerLink;
  }

  closeSidebar(): void {
    this.visibleChange.emit(false);
  }

  onMenuItemClick(): void {
    if (this.isMobile) {
      this.closeSidebar();
    }
  }

  private resolveActiveMenuLink(url: string): string | null {
    const normalizedUrl = this.normalizeCalendarUrl(url);
    const visibleLinks = this.menuItems
      .filter((item): item is Extract<SidebarItem, { type: 'item' }> => item.type === 'item')
      .filter((item) => item.visible && (!item.ownerOnly || this.isOwner()))
      .map((item) => item.routerLink)
      .sort((left, right) => right.length - left.length);

    for (const link of visibleLinks) {
      if (this.matchesLink(normalizedUrl, link)) {
        return link;
      }
    }

    return null;
  }

  private matchesLink(url: string, link: string): boolean {
    if (link === '/') {
      return url === '/' || url === '';
    }

    return url === link || url.startsWith(`${link}/`);
  }

  private normalizeUrl(url: string): string {
    const withoutQuery = url.split('?')[0]?.split('#')[0] ?? '/';
    if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
      return withoutQuery.slice(0, -1);
    }

    return withoutQuery || '/';
  }

  private normalizeCalendarUrl(url: string): string {
    if (url === '/timesheet/owner') {
      return '/timesheet';
    }

    if (url === '/timesheet/owner/calendar') {
      return '/timesheet/calendar';
    }

    return url;
  }
}
